import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { EntityFieldConfig } from '../translations/entity-field-translations';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';

// Helper function to normalize string booleans to actual booleans
const normalizeConfigBooleans = (config: EntityFieldConfig): EntityFieldConfig => {
  const normalized = { ...config };
  
  Object.keys(normalized).forEach(entityType => {
    if (normalized[entityType]?.fields) {
      Object.keys(normalized[entityType].fields).forEach(fieldName => {
        const field = normalized[entityType].fields[fieldName];
        if (field && typeof field.emailEnabled === 'string') {
          field.emailEnabled = field.emailEnabled === 'true';
        }
      });
    }
  });
  
  return normalized;
};

// Types for mentioned entities in emails
export interface MentionedEntity {
  id: string;
  type: string; // entity type like 'building', 'company', etc.
  resourceId: string;
  name: string;
  index: number; // For numbering like "bedrijf1", "gebouw2"
  data?: any; // Full entity data when fetched
}

interface EntityFieldContextType {
  // Field configuration
  fieldConfig: EntityFieldConfig;
  setFieldConfig: (config: EntityFieldConfig) => void;
  
  // Mentioned entities (for email dynamic fields)
  mentionedEntities: MentionedEntity[];
  setMentionedEntities: (entities: MentionedEntity[]) => void;
  addMentionedEntity: (entity: MentionedEntity) => void;
  removeMentionedEntity: (entityId: string) => void;
  
  // Utility functions
  getAvailableFields: (entityType: string) => string[];
  getEntityDisplayName: (entityType: string) => string;
  getFieldDisplayName: (entityType: string, fieldName: string) => string;
  getDynamicFieldName: (entity: MentionedEntity, fieldName: string) => string;
  
  // API functions
  loadFieldConfig: () => Promise<void>;
  updateFieldConfig: (config: EntityFieldConfig) => Promise<EntityFieldConfig>;
  
  // Loading state
  isLoading: boolean;
}

const EntityFieldContext = createContext<EntityFieldContextType | undefined>(undefined);

export const useEntityFields = () => {
  const context = useContext(EntityFieldContext);
  if (!context) {
    throw new Error('useEntityFields must be used within an EntityFieldProvider');
  }
  return context;
};

interface EntityFieldProviderProps {
  children: ReactNode;
}

export const EntityFieldProvider: React.FC<EntityFieldProviderProps> = ({ children }) => {
  const { session } = useSession();
  const [fieldConfig, setFieldConfig] = useState<EntityFieldConfig>({});
  const [mentionedEntities, setMentionedEntities] = useState<MentionedEntity[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load field configuration from backend
  const loadFieldConfig = async () => {
    if (!session) return;
    
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/email/entity-field-config`,
        { method: 'GET' },
        session
      );
      
      if (response.ok) {
        const config = await response.json();
        const normalizedConfig = normalizeConfigBooleans(config);
        setFieldConfig(normalizedConfig);
      } else {
        console.warn('Failed to load field config from backend');
      }
    } catch (error) {
      console.error('Error loading field config:', error);
      // Field config will remain empty until backend is available
    } finally {
      setIsLoading(false);
    }
  };

  // Update field configuration on backend
  const updateFieldConfig = async (config: EntityFieldConfig): Promise<EntityFieldConfig> => {
    if (!session) throw new Error('No session available');
    
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/email/entity-field-config`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(config)
        },
        session
      );
      
      if (response.ok) {
        const updatedConfig = await response.json();
        const normalizedConfig = normalizeConfigBooleans(updatedConfig);
        setFieldConfig(normalizedConfig);
        return normalizedConfig;
      } else {
        throw new Error('Failed to update field config');
      }
    } catch (error) {
      console.error('Error updating field config:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Add a mentioned entity with automatic numbering
  const addMentionedEntity = (entity: MentionedEntity) => {
    setMentionedEntities(prev => {
      // Calculate the next index for this entity type
      const sameTypeEntities = prev.filter(e => e.type === entity.type);
      const nextIndex = sameTypeEntities.length > 0 
        ? Math.max(...sameTypeEntities.map(e => e.index)) + 1 
        : 1;
      
      const newEntity = {
        ...entity,
        index: nextIndex
      };
      
      return [...prev, newEntity];
    });
  };

  // Remove a mentioned entity
  const removeMentionedEntity = (entityId: string) => {
    setMentionedEntities(prev => prev.filter(e => e.id !== entityId));
  };

  // Get available fields for an entity type
  const getAvailableFields = (entityType: string): string[] => {
    const config = fieldConfig[entityType.toLowerCase()];
    if (!config) return [];
    
    return Object.keys(config.fields).filter(fieldName => 
      config.fields[fieldName].emailEnabled
    );
  };

  // Get entity display name
  const getEntityDisplayName = (entityType: string): string => {
    const config = fieldConfig[entityType.toLowerCase()];
    if (config?.displayName) {
      return config.displayName;
    }
    return entityType;
  };

  // Get field display name (Dutch translation)
  const getFieldDisplayName = (entityType: string, fieldName: string): string => {
    const config = fieldConfig[entityType.toLowerCase()];
    if (config?.fields[fieldName]?.dutchName) {
      return config.fields[fieldName].dutchName;
    }
    return fieldName;
  };

  // Generate dynamic field name like {bedrijf1.naam}
  const getDynamicFieldName = (entity: MentionedEntity, fieldName: string): string => {
    const entityDisplayName = getEntityDisplayName(entity.type).toLowerCase();
    const fieldDisplayName = getFieldDisplayName(entity.type, fieldName);
    return `{${entityDisplayName}${entity.index}.${fieldDisplayName}}`;
  };

  // Load configuration on mount
  useEffect(() => {
    if (session) {
      loadFieldConfig();
    }
  }, [session]);

  const value: EntityFieldContextType = {
    fieldConfig,
    setFieldConfig,
    mentionedEntities,
    setMentionedEntities,
    addMentionedEntity,
    removeMentionedEntity,
    getAvailableFields,
    getEntityDisplayName,
    getFieldDisplayName,
    getDynamicFieldName,
    loadFieldConfig,
    updateFieldConfig,
    isLoading
  };

  return (
    <EntityFieldContext.Provider value={value}>
      {children}
    </EntityFieldContext.Provider>
  );
}; 